
import React, { useCallback, useMemo, useEffect } from 'react'
import { useStore, type NodeProps } from '@xyflow/react'
import { ImageFile } from '@/components/canvas/nodes'
import type { GenieNodeState } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'

/**
 * ImageFileNode - React Flow custom node
 *
 * Replaces ImageFileAdapter for the React Flow migration.
 * Wraps the existing ImageFile variant component with React Flow handles.
 */

export interface ImageFileNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
  onDimensionsChange?: (dimensions: { width: number; height: number }) => void
}

function ImageFileNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as ImageFileNodeData
  const { state: genieState, onHoverChange, onDimensionsChange } = nodeData
  const onStateChange = useNodeStateChange(id)

  // Quantized zoom — only 2 values to avoid re-renders on every zoom frame.
  // ImageFile uses zoom < 1.0 threshold for low-res vs high-res switching.
  const quantizedZoom = useStore((s) => s.transform[2] >= 1.0 ? 1.0 : 0.5)

  // Stable onFileChange callback
  const handleFileChange = useCallback((fileUuid: string, metadata: Record<string, unknown>) => {
    onStateChange((prevState: Record<string, unknown>) => ({
      ...prevState,
      fileUuid,
      fileMetadata: metadata,
    }))
  }, [onStateChange])

  const handleSelect = useCallback(() => {
    // Selection handled by React Flow
  }, [])

  // Programmatic action handler
  const action = genieState._action
  const actionTimestamp = action?.ts

  useEffect(() => {
    if (!action) return

    switch (action.type) {
      case 'clear':
        onStateChange(prev => ({ ...prev, fileUuid: undefined, fileMetadata: undefined }))
        break
      case 'update':
        if (action.params?.fileUuid !== undefined) {
          onStateChange(prev => ({
            ...prev,
            fileUuid: action.params.fileUuid,
            fileMetadata: action.params.fileMetadata,
          }))
        }
        break
    }

    onStateChange(prev => ({ ...prev, _action: null }))
  }, [actionTimestamp, action, onStateChange])

  const fileProps = useMemo(
    () => ({
      id,
      title: genieState.title,
      selected: selected || genieState.selected,
      fileUuid: genieState.fileUuid as string | undefined,
      canvasId: genieState.canvasId as string | undefined,
      zoom: quantizedZoom,
      hasParent: !!genieState.parentNodeId,
      onFileChange: handleFileChange,
      onSelect: handleSelect,
      onHoverChange,
      onDimensionsChange,
      onStateChange,
      state: genieState,
    }),
    [id, genieState, selected, quantizedZoom, handleFileChange, handleSelect, onHoverChange, onDimensionsChange, onStateChange],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.ImageFile

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <ImageFile {...fileProps} />
    </>
  )
}

export const ImageFileNode = React.memo(
  ImageFileNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as ImageFileNodeData
    const nextData = nextProps.data as unknown as ImageFileNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.fileUuid === nextData.state.fileUuid &&
      prevData.state.canvasId === nextData.state.canvasId &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.state.fileMetadata === nextData.state.fileMetadata &&
      prevData.state.generationStatus === nextData.state.generationStatus &&
      prevData.state.generationProgress === nextData.state.generationProgress &&
      prevData.state._action === nextData.state._action &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

ImageFileNode.displayName = 'ImageFileNode'
